import { useParams } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useGetMaterialBySlugQuery } from '../services/materialsApi';
import { useAddToHistoryMutation } from '../services/historyApi';
import { useWatchTracker } from '../hooks/useWatchTracker';
import TelegramBanner from '../components/TelegramBanner';
import styles from '../css/Watch.module.css';
import kinopoiskLogo from '../assets/icons/kinopoisk.jpg';
import imdbLogo from '../assets/icons/IMDB_Logo_2016.svg';
import mydramalistLogo from '../assets/icons/mydramalist.png';
import BookmarkButton from '../components/BookmarkButton';

const Watch = () => {
  const { slugParam } = useParams();

  const [slug, slugId] = slugParam.includes('--')
    ? slugParam.split('--')
    : [null, slugParam];

  const { data, isLoading, error } = useGetMaterialBySlugQuery(
    { slug, slugId },
    { skip: !slug || !slugId }
  );

  const drama = data?.data;

  const isAuthenticated = useSelector(s => s.auth.isAuthenticated);
  const [addToHistory] = useAddToHistoryMutation();

  // Длительность текущего эпизода — приходит отдельным событием от Kodik
  const durationRef = useRef(0);

  const { reportProgress, reportEpisodeChange } = useWatchTracker({
    materialId: drama?.id,
    enabled: isAuthenticated && !!drama,
    threshold: 0.5,
    onWatched: ({ materialId, episode }) => {
      addToHistory({ materialId, episode });
    },
  });

  // Kodik-адаптер: переводит postMessage-события в вызовы хука
  useEffect(() => {
    if (!drama || !isAuthenticated) return;

    const handleMessage = (event) => {
      let data;
      try {
        data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }
      if (!data?.key) return;

      // Продолжительность — приходит при старте и смене качества
      if (data.key === 'kodik_player_duration_update') {
        durationRef.current = data.value;
      }

      // Текущее время — раз в секунду
      if (data.key === 'kodik_player_time_update') {
        reportProgress(data.value, durationRef.current);
      }

      // Смена серии (для сериалов; у фильмов episode и season = null)
      if (data.key === 'kodik_player_current_episode') {
        const episode = data.value?.episode ?? 0;
        reportEpisodeChange(episode);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [drama, isAuthenticated, reportProgress, reportEpisodeChange]);

  if (isLoading) {
    return (
      <div className={styles.watchPage}>
        <p>Загрузка...</p>
      </div>
    );
  }

  if (error || !drama) {
    return (
      <div className={styles.watchPage}>
        <p>Дорама не найдена</p>
      </div>
    );
  }

  const genres = drama.all_genres || drama.genres || drama.drama_genres || [];
  const translations = drama.translations || [];
  const voices = translations.filter(t => t.type === 'voice').map(t => t.team_title);
  const subtitles = translations.filter(t => t.type === 'subtitles').map(t => t.team_title);
  const ratings = [
    { label: 'KinoPoisk', value: drama.kinopoisk_rating, logo: kinopoiskLogo },
    { label: 'IMDb', value: drama.imdb_rating, logo: imdbLogo },
    { label: 'MyDramaList', value: drama.mydramalist_rating, logo: mydramalistLogo },
  ].filter(r => r.value && r.value > 0);
  const countries = drama.countries || [];

  const statusLabels = {
    ongoing: 'Онгоинг',
    released: 'Выпущен',
    anons: 'Анонс',
    announced: 'Анонс',
    completed: 'Завершён',
  };
  const statusLabel = drama.drama_status
    ? (statusLabels[drama.drama_status] ?? drama.drama_status)
    : null;

  return (
    <div className={styles.watchPage}>
      <div className={styles.pageHeader}>
        <button onClick={() => window.history.back()} className={styles.backButton}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Назад
        </button>
      </div>

      <div className={styles.infoWrapper}>
        <div className={styles.posterColumn}>
          {drama.poster_url && drama.poster_url !== 'no-poster' ? (
            <img
              src={`https://doramahype.ru/uploads/posters/${drama.poster_url}`}
              alt={drama.title}
            />
          ) : (
            <div className={styles.noPosterWatch}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <path d="M21 15l-5-5L5 21"/>
              </svg>
              <span>Постер недоступен</span>
            </div>
          )}
          <div className={styles.posterActions}>
            <BookmarkButton
              materialId={drama.id}
              initialBookmark={drama.user_meta?.bookmark}
            />
          </div>
        </div>

        <div className={styles.infoContainer}>
          <div className={styles.infoContent}>
            <h1 className={styles.infoTitle}>{drama.title || drama.title_original}</h1>
            {(drama.other_title || drama.title_eng || drama.title_orig) && (
              <p className={styles.otherTitle}>
                {drama.other_title || drama.title_eng || drama.title_orig}
              </p>
            )}

            <div className={styles.infoMeta}>
              {drama.year && <span className={styles.metaBadge}>{drama.year}</span>}
              {countries.length > 0 && <span className={styles.metaBadge}>{countries.join(', ')}</span>}
              {drama.episodes_count && <span className={styles.metaBadge}>{drama.episodes_count} серий</span>}
              {drama.minimal_age && <span className={styles.metaBadge}>{drama.minimal_age}+</span>}
            </div>

            {ratings.length > 0 && (
              <div className={styles.ratingsBlock}>
                {ratings.map(r => (
                  <div key={r.label} className={styles.ratingItem}>
                    <img src={r.logo} alt={r.label} className={styles.ratingLogo} />
                    <span className={styles.ratingSource}>{r.label}</span>
                    <span className={styles.ratingValue}>{r.value}</span>
                  </div>
                ))}
              </div>
            )}

            {drama.description && (
              <p className={styles.infoDescription}>{drama.description}</p>
            )}

            <div className={styles.infoDetails}>
              {statusLabel && (
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Статус:</span>
                  <span className={styles.detailValue}>{statusLabel}</span>
                </div>
              )}
              {genres.length > 0 && (
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Жанры:</span>
                  <span className={styles.detailValue}>{genres.join(', ')}</span>
                </div>
              )}
            </div>

            {(voices.length > 0 || subtitles.length > 0) && (
              <div className={styles.translationsBlock}>
                {voices.length > 0 && (
                  <div className={styles.translationRow}>
                    <span className={styles.translationLabel}>Озвучка</span>
                    <span className={styles.translationValue}>{voices.join(', ')}</span>
                  </div>
                )}
                {subtitles.length > 0 && (
                  <div className={styles.translationRow}>
                    <span className={styles.translationLabel}>Субтитры</span>
                    <span className={styles.translationValue}>{subtitles.join(', ')}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

      </div>

      <div className={styles.playerSection}>
        <div className={styles.playerContainer}>
          <div className={styles.videoWrapper}>
            {drama.active_link ? (
              <iframe
                src={
                  drama.type === 'foreign-serial'
                    ? `${drama.active_link}?episode=1`
                    : drama.active_link
                }
                className={styles.kodikPlayer}
                allowFullScreen
                allow="autoplay *; fullscreen *"
                title={drama.title}
              />
            ) : (
              <div className={styles.noPlayer}>Видео недоступно</div>
            )}
          </div>
        </div>
        <div className={styles.adBanner}>
          <TelegramBanner />
        </div>
      </div>

      <div className={styles.adBannerMobile}>
        <TelegramBanner />
      </div>
    </div>
  );
};

export default Watch;
